const post = {
  "slug": "sell-without-etsy-fees-instagram-flat-monthly-fee",
  "title": "Sell Without Etsy Fees: Use Instagram DMs and Pay a Flat Monthly Fee",
  "seoTitle": "Skip Etsy Fees: Sell on Instagram, Flat Fee",
  "description": "Etsy takes a listing fee and a percentage of every sale. See the numbers, and how selling through Instagram DMs with Checkout DM swaps them for one flat fee.",
  "excerpt": "Etsy takes a percentage of every order. Checkout DM charges one fixed monthly price and no commission. Here is what that difference looks like at 50, 100 and 200 sales.",
  "category": "Comparisons",
  "tags": [
    "Etsy fees",
    "Etsy alternative",
    "Sell without Etsy",
    "No commission selling"
  ],
  "date": "2026-11-23",
  "cover": {
    "src": "/blog-images/sell-without-etsy-fees-instagram-flat-monthly-fee-cover.png",
    "alt": "A per-sale marketplace fee compared with a single flat monthly plan price"
  },
  "blocks": [
    {
      "type": "p",
      "text": "Etsy is a good place to be found by buyers, but it charges you on every sale. If you already have people following your work on Instagram, you may be paying a marketplace for customers you brought yourself. This guide shows what Etsy\u2019s per-sale fees add up to, and how selling through Instagram DMs with Checkout DM replaces them with one flat monthly price."
    },
    {
      "type": "h2",
      "text": "What Etsy charges"
    },
    {
      "type": "p",
      "text": "Etsy publishes its fees on its fees and payments policy page, and they can change, so confirm the current figures for your country before you decide. Based on its published US rates in mid-2026, a typical sale carries these costs:"
    },
    {
      "type": "ul",
      "items": [
        "**A listing fee** of $0.20 for each listing.",
        "**A transaction fee** of 6.5% of the order, including shipping.",
        "**A payment processing fee,** which depends on your country. For US sellers it is 3% plus $0.25 per order.",
        "**Optional advertising fees,** if you use Etsy\u2019s ad tools, and a currency conversion fee when your listing currency differs from your payment account currency."
      ]
    },
    {
      "type": "p",
      "text": "The transaction fee is the one that grows with your success. It takes a slice of every order, and the bigger your sales, the more you pay."
    },
    {
      "type": "h2",
      "text": "What Checkout DM charges"
    },
    {
      "type": "p",
      "text": "Checkout DM charges a fixed monthly plan price. It takes no commission and no transaction fee on your orders, and payments go straight to your own payment account or bank account. There is nothing to pay per listing and nothing to pay per sale. The plans are Free (\u20b90), Creator (\u20b9499 or $9), Starter (\u20b91,499 or $19), Growth (\u20b92,899 or $39) and Pro (\u20b96,499 or $69) per month, with the details in our guide to [Checkout DM pricing and plans](/blog/checkout-dm-pricing-and-plans-explained)."
    },
    {
      "type": "callout",
      "tone": "lilac",
      "title": "Be clear about the gateway",
      "text": "A payment gateway such as Razorpay, Stripe or PayPal charges its own fee for processing a card or UPI payment. You would pay a processing fee on Etsy too, so this comparison leaves processing fees out on both sides and looks only at what the platform itself takes."
    },
    {
      "type": "h2",
      "text": "The numbers at 50, 100 and 200 sales"
    },
    {
      "type": "p",
      "text": "Here is Etsy\u2019s transaction fee alone on an item priced at $30, with no shipping charged. At 6.5% that is $1.95 per sale. Listing fees, processing fees and ads would all be on top."
    },
    {
      "type": "table",
      "headers": [
        "Sales a month",
        "Etsy transaction fee only",
        "Checkout DM Starter",
        "Checkout DM Growth"
      ],
      "rows": [
        [
          "50",
          "$97.50",
          "$19 flat",
          "$39 flat"
        ],
        [
          "100",
          "$195.00",
          "$19 flat",
          "$39 flat"
        ],
        [
          "200",
          "$390.00",
          "$19 flat",
          "$39 flat"
        ]
      ]
    },
    {
      "type": "p",
      "text": "Starter is the first plan with unlimited conversations, which is why it is the fair comparison once you have more than a handful of orders. The Free plan costs nothing but is limited to 5 products and 15 customer conversations a month, which makes it a good way to test the idea before you spend anything."
    },
    {
      "type": "h2",
      "text": "What you give up by leaving Etsy"
    },
    {
      "type": "p",
      "text": "Etsy\u2019s fees buy you something real: buyers who are already searching for handmade and craft items. If you have no audience, a marketplace can bring you first sales while you build one. Selling through Instagram means you bring your own customers through posts, Reels and Stories. That is more work at the start, but the customers and the relationship belong to you. We compare the two channels in more depth in [Etsy versus Instagram selling](/blog/etsy-vs-instagram-selling-india), including the rules Etsy has for sellers in India."
    },
    {
      "type": "h2",
      "text": "How the sale works without Etsy"
    },
    {
      "type": "ol",
      "items": [
        "A customer sees your product on a post, Reel or Story and messages you, or comments a keyword that starts a DM.",
        "The Checkout DM bot shows the product, the price and the sizes, and collects the delivery details.",
        "The customer pays through a gateway link or by UPI, QR code or bank transfer.",
        "The order appears in your dashboard, and you ship it."
      ]
    },
    {
      "type": "p",
      "text": "You can also run both. Some sellers keep an Etsy shop for buyers who search the marketplace and use Instagram for the customers they already have. If you do, work out your real margin after fees first. Our guide to [pricing products on Instagram](/blog/price-products-on-instagram-india) shows how."
    },
    {
      "type": "h2",
      "text": "A simple way to test it"
    },
    {
      "type": "ul",
      "items": [
        "Pick the three products you sell most on Etsy or would like to sell.",
        "Add them to a Free Checkout DM catalog with clear names, prices and photos.",
        "Mention in your next post how to order: send a message or comment a keyword.",
        "Count how many orders arrive and compare the fees you would have paid on a marketplace."
      ]
    }
  ],
  "related": [
    {
      "to": "/pricing",
      "title": "Pricing",
      "description": "Flat monthly plans with no commission."
    },
    {
      "to": "/help",
      "title": "Help Guide",
      "description": "How a DM checkout works end to end."
    },
    {
      "to": "/catalog-guide",
      "title": "Catalog Guide",
      "description": "Set up your products for DM selling."
    }
  ],
  "faqs": [
    {
      "q": "Does Checkout DM take a percentage of my sales?",
      "a": "No. Checkout DM charges a fixed monthly plan price and takes no commission and no transaction fee on your orders."
    },
    {
      "q": "What are Etsy\u2019s main seller fees?",
      "a": "A $0.20 listing fee, a 6.5% transaction fee on the order including shipping and a payment processing fee that depends on your country. Check Etsy\u2019s fees and payments policy for your current rates."
    },
    {
      "q": "Do I still pay a payment gateway fee with Checkout DM?",
      "a": "Yes, if you use a gateway such as Razorpay, Stripe or PayPal. The gateway charges its own processing fee, as it would on any platform. UPI, QR code and bank transfer payments can be confirmed manually without a gateway."
    },
    {
      "q": "Which plan should I compare with Etsy?",
      "a": "Starter is the first plan with unlimited conversations, at \u20b91,499 or $19 a month. The Free plan is limited to 5 products and 15 conversations a month, so it is best for testing."
    },
    {
      "q": "Can I sell on Etsy and Instagram together?",
      "a": "Yes. Many sellers keep a marketplace shop for buyers who search it and use Instagram for the customers they already have."
    }
  ]
};

export default post;
